import { useEffect, useState } from 'react'

/** Types we paint ourselves rather than handing to an iframe. */
export function isTextualShare(mime: string): boolean {
  const m = (mime || '').toLowerCase().split(';')[0].trim()
  return (
    m === 'text/csv' ||
    m === 'text/plain' ||
    m === 'text/markdown' ||
    m === 'application/json'
  )
}

/**
 * Read a shared text file so the app can render it directly.
 *
 * An iframe is at the mercy of the response headers: the store marks anything
 * outside its inline-safe list `Content-Disposition: attachment`, and a browser
 * given that saves the file instead of showing it — the presenter and every
 * viewer get a download and a blank frame. Painting the text ourselves takes
 * the headers out of it entirely, which is the only version of this that cannot
 * regress on a storage or deployment change.
 *
 * Reads the object URL the share hook already produced, so this is a local read
 * of bytes that are in memory — not a second trip to the network.
 */
export function useSharedTextContent(
  objectUrl: string,
  mime: string,
): { text: string; loading: boolean; error: string | null } {
  const [text, setText] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!objectUrl || !isTextualShare(mime)) {
      setText('')
      setError(null)
      setLoading(false)
      return
    }
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    fetch(objectUrl, { signal: controller.signal })
      .then((res) => res.text())
      .then((body) => setText(body))
      .catch((err: unknown) => {
        if (controller.signal.aborted) return
        setError(err instanceof Error ? err.message : 'Could not read the shared file.')
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [objectUrl, mime])

  return { text, loading, error }
}

/**
 * Split one CSV line into fields.
 *
 * Hand-rolled rather than split(',') because a quoted field may contain commas
 * and doubled quotes — `"Kanchi, Anitha"` is one field, `""` inside a quoted
 * field is a literal quote. Splitting naively turns a perfectly ordinary
 * spreadsheet into misaligned columns, which looks like corruption.
 */
function parseCsvLine(line: string): string[] {
  const out: string[] = []
  let field = ''
  let quoted = false
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (quoted) {
      if (c === '"') {
        if (line[i + 1] === '"') { field += '"'; i++ } else quoted = false
      } else field += c
    } else if (c === '"') quoted = true
    else if (c === ',') { out.push(field); field = '' }
    else field += c
  }
  out.push(field)
  return out
}

/** CSV text as rows of cells. Capped — a shared spreadsheet is for reading in
 *  the meeting, not for rendering ten thousand DOM rows into a video call. */
export function parseCsv(text: string, maxRows = 500): { rows: string[][]; truncated: boolean } {
  const lines = text.replace(/\r\n?/g, '\n').split('\n').filter((l) => l.length > 0)
  const rows = lines.slice(0, maxRows).map(parseCsvLine)
  return { rows, truncated: lines.length > maxRows }
}
